import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { Card } from "antd";
import { ExportToExcel } from "../../../../utils";

export const ChartStatus = ({ data }) => {
  const [chartData, setChartData] = useState([]);
  const [detailData, setDetailData] = useState([]);

  useEffect(() => {
    const processedData = processData(data);
    const exportStatusDetailData = exportDetailData(data);
    setChartData(processedData);
    setDetailData(exportStatusDetailData);
  }, [data]);

  const processData = (data) => {
    let ordersByStatus = {};
    data.forEach((order) => {
      if (!ordersByStatus[order.status]) {
        ordersByStatus[order.status] = {
          totalSales: 0,
          totalItems: 0,
          totalOrders: 0,
        };
      }
      order.products.forEach((item) => {
        ordersByStatus[order.status].totalItems += item.quantity;
      });
      ordersByStatus[order.status].totalSales += order.saleAmount;
      ordersByStatus[order.status].totalOrders++;
    });

    const chartData = Object.keys(ordersByStatus)?.map((status) => {
      const statusData = ordersByStatus[status];
      return {
        status,
        totalSales: statusData.totalSales,
        totalItems: statusData.totalItems,
        totalOrders: statusData.totalOrders,
        averageTicket: statusData.totalItems / statusData.totalOrders,
      };
    });

    const sortedChartData = chartData?.sort((a, b) => b.totalOrders - a.totalOrders);
    return sortedChartData;
  };

  const exportDetailData = (data) => {
    let ordersByStatus = {};
    const exportDataArray = [];
    data.forEach((order) => {
      order.products.forEach((item) => {
        if(!ordersByStatus[order.status]){
          ordersByStatus[order.status] = {};
        }
        if(!ordersByStatus[order.status][order.customerName]){
          ordersByStatus[order.status][order.customerName] = {};
        }
        if(!ordersByStatus[order.status][order.customerName][item.brand]){
          ordersByStatus[order.status][order.customerName][item.brand] = {};
        }
        if(!ordersByStatus[order.status][order.customerName][item.brand][item.category]){
          ordersByStatus[order.status][order.customerName][item.brand][item.category] = {};
        }
        if(!ordersByStatus[order.status][order.customerName][item.brand][item.category][item.color]){
          ordersByStatus[order.status][order.customerName][item.brand][item.category][item.color] = {};
        }
        if(!ordersByStatus[order.status][order.customerName][item.brand][item.category][item.color][item.size]){
          ordersByStatus[order.status][order.customerName][item.brand][item.category][item.color][item.size] = {};
        }
        if(!ordersByStatus[order.status][order.customerName][item.brand][item.category][item.color][item.size][item.model]){
          ordersByStatus[order.status][order.customerName][item.brand][item.category][item.color][item.size][item.model] = {
            phoneNumber : order.phoneNumber,
            totalSales: 0,
            totalItems: 0,
            totalOrders: 0,
          };
        }
        ordersByStatus[order.status][order.customerName][item.brand][item.category][item.color][item.size][item.model].totalSales +=
          item.total * item.quantity;
        ordersByStatus[order.status][order.customerName][item.brand][item.category][item.color][item.size][item.model].totalItems += item.quantity;
        ordersByStatus[order.status][order.customerName][item.brand][item.category][item.color][item.size][item.model].totalOrders++;
      });
    });
    // eslint-disable-next-line
    Object.keys(ordersByStatus)?.map((status) => {
      // eslint-disable-next-line
      Object.keys(ordersByStatus[status])?.map((customerName) => {
        // eslint-disable-next-line
        Object.keys(ordersByStatus[status][customerName])?.map((brand) => {
          // eslint-disable-next-line
          Object.keys(ordersByStatus[status][customerName][brand])?.map((category) => {
            // eslint-disable-next-line
            Object.keys(ordersByStatus[status][customerName][brand][category])?.map((color)=>{
              // eslint-disable-next-line
              Object.keys(ordersByStatus[status][customerName][brand][category][color])?.map((size)=>{
                // eslint-disable-next-line
                Object.keys(ordersByStatus[status][customerName][brand][category][color][size])?.map((model)=>{
                  const record = {
                    status,
                    customerName,
                    brand,
                    category,
                    color,
                    size,
                    model,
                    ...ordersByStatus[status][customerName][brand][category][color][size][model],
                    averageTicket : ordersByStatus[status][customerName][brand][category][color][size][model].totalItems / 
                    ordersByStatus[status][customerName][brand][category][color][size][model].totalOrders,
                  };
                  exportDataArray.push(record);
                });
              });
            });
          });
        });
      });
    });
    return exportDataArray;
  }

  const renderCustomizedLabel = (props) => {
    const { x, y, width, value } = props;

    return (
      <text
        x={x + width + 5}
        y={y + 15}
        fill="#000"
        textAnchor="start"
        dominantBaseline="start"
        style={{ fontSize: "14px" }}
      >
        {value}
      </text>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const { totalSales, totalItems, totalOrders, averageTicket } =
        payload[0].payload;

      return (
        <div
          style={{
            backgroundColor: "#fff",
            border: "1px solid #ccc",
            padding: "10px",
          }}
        >
          <b className="intro">{label}</b>
          <p className="intro">{`Ventas totales: S/ ${totalSales?.toFixed(
            2
          )}`}</p>
          <p className="desc">{`Cantidad de órdenes: ${totalOrders}`}</p>
          <p className="desc">{`Cantidad de productos: ${totalItems}`}</p>
          <p className="desc">{`Ticket promedio: ${averageTicket?.toFixed(
            2
          )}`}</p>
        </div>
      );
    }

    return null;
  };

  const exportData = detailData?.map((item) => ({
    Estado: item.status,
    Cliente : item.customerName,
    Telefono : item.phoneNumber,
    Marca : item.brand,
    Categoria : item.category,
    Color : item.color,
    Talla : item.size,
    Modelo : item.model,
    "Ventas totales": `S/ ${item.totalSales?.toFixed(2)}`,
    "Cantidad de órdenes": item.totalOrders,
    "Cantidad de productos": item.totalItems,
    "Ticket promedio": item.averageTicket?.toFixed(2),
  }));

  const exportColumns = [
    { title: "Estado", dataIndex: "Estado" },
    { title: "Cliente", dataIndex: "Cliente"},
    { title: "Telefono", dataIndex: "Telefono"},
    { title: "Marca", dataIndex: "Marca"},
    { title: "Categoria", dataIndex: "Categoria"},
    { title: "Color", dataIndex: "Color"},
    { title: "Talla" , dataIndex: "Talla"},
    { title: "Modelo" , dataIndex: "Modelo"},
    { title: "Ventas totales", dataIndex: "Ventas totales" },
    { title: "Cantidad de órdenes", dataIndex: "Cantidad de órdenes" },
    { title: "Cantidad de productos", dataIndex: "Cantidad de productos" },
    { title: "Ticket promedio", dataIndex: "Ticket promedio" },
  ];

  return (
    <Card title="Estados">
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{
            left: 65,
            right: 50,
            top: 10,
            bottom: 10,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.5} />
          <XAxis type="number" dataKey="totalOrders" />
          <YAxis
            dataKey="status"
            type="category"
            tick={{ fontSize: "14px", width: 100 }}
            interval={0}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="totalOrders" fill="#2B767E">
            <LabelList dataKey="totalOrders" content={renderCustomizedLabel} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 10,
          marginRight: 20,
        }}
      >
        <ExportToExcel
          data={exportData}
          columns={exportColumns}
          fileName="resumenEstados"
          showView={false}
        />
      </div>
    </Card>
  );
};
